'use client'

import { Fragment, type ReactNode } from 'react'
import { cn } from '@/lib/utils'
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from '@/components/ui/breadcrumb'
import { Separator } from '@/components/ui/separator'
import { SidebarTrigger } from '@/components/ui/sidebar'
import { HeaderWorkspaceLogo } from '@/components/brand/header-workspace-logo'
import { ENGLISH_MESSAGES } from '@/lib/i18n/catalogues'
import { useShellTranslation } from '@/lib/shell/use-shell-translation'

/**
 * The catalogue namespaces the navigation's titles live in: every sidebar item
 * (components/app-sidebar.tsx) and every settings-hub card (lib/settings-nav.ts)
 * names its words by an `i18nKey` in one of these, and its English title is
 * that key's English text.
 */
const NAV_TITLE_NAMESPACES = ['shell.nav', 'nav', 'shell.settingsNav', 'vishing.nav'] as const

let navTitleKeys: ReadonlyMap<string, string> | undefined

/** English navigation title -> its catalogue key, read once from the English catalogue. */
function collectNavTitleKeys(): ReadonlyMap<string, string> {
  const keys = new Map<string, string>()
  const walk = (node: unknown, path: string) => {
    if (typeof node === 'string') {
      if (!keys.has(node)) keys.set(node, path)
      return
    }
    if (node === null || typeof node !== 'object') return
    for (const [segment, child] of Object.entries(node)) {
      // A settings card's description is a sentence about the page, never a crumb.
      if (segment === 'descriptions') continue
      walk(child, `${path}.${segment}`)
    }
  }
  for (const namespace of NAV_TITLE_NAMESPACES) {
    const tree = namespace.split('.').reduce<unknown>(
      (node, segment) => (node && typeof node === 'object' ? (node as Record<string, unknown>)[segment] : undefined),
      ENGLISH_MESSAGES,
    )
    walk(tree, namespace)
  }
  return keys
}

/**
 * The catalogue key of a navigation title ("Phishing" -> shell.nav.phishing),
 * or undefined when `label` is not one (a campaign name, a label the page has
 * already translated).
 *
 * Built from the catalogue the navigation reads its words from, not by
 * importing the sidebar: PageShell is on every admin page, and the sidebar
 * module pulls in its hooks, stores and API client. The spec
 * components/ui/__tests__/page-shell-crumbs-speak-the-sidebars-words.spec.tsx
 * walks the real sidebar and settings navigation and fails if any of their
 * titles would read differently here.
 */
export function navLabelKey(label: string): string | undefined {
  navTitleKeys ??= collectNavTitleKeys()
  return label ? navTitleKeys.get(label) : undefined
}

/**
 * Canonical page chrome for every admin page. Fills the sidebar inset and
 * owns vertical scroll so UnifiedDataTable sticky headers cannot paint over
 * the breadcrumb bar. Sticky header (z-30) + scrollable body.
 *
 * Usage:
 *
 *   <PageShell breadcrumbs={[
 *     { label: 'Phishing', href: '/phishing' },
 *     { label: 'Campaigns' },
 *   ]}>
 *     <YourContent />
 *   </PageShell>
 *
 * A root crumb, "Dashboard" (`/`) in the interface language, is rendered
 * before the first crumb automatically; `rootLabel` / `rootHref` replace it.
 * The last crumb (no href) renders as <BreadcrumbPage> (the current page).
 *
 * A crumb label that is one of the sidebar's titles ("Phishing", "Reports",
 * "Settings") reads in the interface language, through the same catalogue key
 * the sidebar uses; any other label (an entity name, a label the page already
 * translated) is shown as given. The same holds for a `rootLabel`: a module
 * root that is a sidebar title ("Gamification", "Email Security (ESA)") reads
 * in the sidebar's words, any other ("Admin") as given.
 *
 * For pages that need custom header actions, pass `headerActions`. They
 * land at the inline end of the sticky header: the right in English, the
 * left in Arabic.
 *
 * For full-bleed editor pages that don't want the standard `gap-4 p-6`
 * body padding, pass `bodyClassName="flex flex-1 flex-col"` (or any
 * other override).
 */
export interface PageShellCrumb {
  label: string
  /** Omit on the last crumb to render it as the current page. */
  href?: string
}

interface PageShellProps {
  breadcrumbs: PageShellCrumb[]
  children: ReactNode
  /** Optional content rendered at the inline end of the sticky header (the right in English, the left in Arabic). */
  headerActions?: ReactNode
  /** Override the body wrapper className. Default: `flex flex-1 flex-col gap-4 p-6`. */
  bodyClassName?: string
  /**
   * Override the dashboard root link label. Default: "Dashboard" in the interface
   * language (`shell.nav.dashboard`). A sidebar title reads in the sidebar's words.
   */
  rootLabel?: string
  /** Override the dashboard root link href. Default: "/". */
  rootHref?: string
}

export function PageShell({
  breadcrumbs,
  children,
  headerActions,
  bodyClassName,
  rootLabel,
  rootHref = '/',
}: PageShellProps) {
  // English outside the i18n provider (bare renders, portals), never a key path.
  const { t } = useShellTranslation()
  // A sidebar title in the sidebar's words; anything else as given.
  const crumbText = (label: string) => {
    const key = navLabelKey(label)
    return key ? t(key) : label
  }
  return (
    <div
      data-testid="page-shell"
      className="flex h-full min-h-0 flex-1 flex-col overflow-hidden"
    >
      {/* pe: the fixed corner controls (HeaderControlsBar, pinned to the
          inline end: top-right in English, top-left in Arabic) float over this
          header and publish their width as --header-controls-inset, so header
          actions stay clear of them; 0 when no controls are mounted. */}
      <header className="sticky top-0 z-30 isolate flex h-16 shrink-0 items-center justify-between gap-2 border-b bg-canvas pe-[var(--header-controls-inset,0px)] transition-[width,height] ease-linear group-has-data-[collapsible=icon]/sidebar-wrapper:h-12">
        <div className="flex min-w-0 items-center gap-2 px-4">
          <SidebarTrigger className="-ms-1" />
          <Separator orientation="vertical" className="me-2 h-4" />
          <HeaderWorkspaceLogo />
          <Breadcrumb>
            <BreadcrumbList>
              <BreadcrumbItem className="hidden md:block">
                <BreadcrumbLink href={rootHref}>
                  {rootLabel !== undefined ? crumbText(rootLabel) : t('shell.nav.dashboard')}
                </BreadcrumbLink>
              </BreadcrumbItem>
              {breadcrumbs.map((crumb, i) => {
                const isLast = i === breadcrumbs.length - 1
                return (
                  <Fragment key={`${crumb.label}-${i}`}>
                    <BreadcrumbSeparator className="hidden md:block" />
                    <BreadcrumbItem
                      className={isLast ? undefined : 'hidden md:block'}
                    >
                      {isLast || !crumb.href ? (
                        <BreadcrumbPage>{crumbText(crumb.label)}</BreadcrumbPage>
                      ) : (
                        <BreadcrumbLink href={crumb.href}>
                          {crumbText(crumb.label)}
                        </BreadcrumbLink>
                      )}
                    </BreadcrumbItem>
                  </Fragment>
                )
              })}
            </BreadcrumbList>
          </Breadcrumb>
        </div>
        {headerActions && (
          <div className="flex items-center gap-2 px-4">{headerActions}</div>
        )}
      </header>

      <div
        className={cn(
          'flex min-h-0 flex-1 flex-col gap-4 overflow-y-auto p-6',
          bodyClassName,
        )}
      >
        {children}
      </div>
    </div>
  )
}
